<template>
    <nav class="flex max-h-full flex-col gap-y-6 bg-white pt-7 text-base">
        <SvgIcon
            class="ml-4 h-7 w-32 shrink-0"
            name="movepro_icon"
        />
        <div class="grid !w-[63px]">
            <template
                v-for="(item, idx) in navigationWithCounts"
                :key="idx"
            >
                <component :is="item?.subitems ? Disclosure : 'div'">
                    <router-link
                        v-if="!item.subitems"
                        v-slot="{ isActive }"
                        :to="{ path: item.route }"
                        @click.prevent="canRefresh(item.route) && $router.go()"
                        @click.ctrl="modClick(item.route)"
                        @click.meta="modClick(item.route)"
                    >
                        <MuvalTippy
                            placement="right"
                            triggers="mouseenter"
                        >
                            <div
                                :key="item"
                                class="icon-hover relative flex h-10 items-center gap-4 overflow-x-clip px-4 text-base font-normal no-underline"
                            >
                                <SvgIcon
                                    :class="[
                                        { '!text-brand': subIsActive(item.route) },
                                        { 'text-muval-gray-1 dark:text-white': !isActive },
                                    ]"
                                    class="z-10 h-6 w-6 flex-shrink-0 transition"
                                    :name="item.icon"
                                />
                                <div
                                    class="bg-icon absolute left-0 top-0 z-0 hidden h-10 w-[63px] bg-muval-gray-6"
                                ></div>

                                <div
                                    v-if="item.meta?.count"
                                    class="z-50 flex size-[18px] items-center justify-center rounded-full bg-brand text-xs font-bold text-white"
                                    style="margin-left: -14px"
                                >
                                    {{ item.meta?.count }}
                                </div>
                            </div>

                            <template #content>
                                {{ item.name }}
                            </template>
                        </MuvalTippy>
                    </router-link>
                    <template v-else>
                        <div class="relative">
                            <DisclosureButton
                                v-slot="{ open }"
                                class="disclosure-button w-full"
                                @click="togglePanel(idx)"
                            >
                                <MuvalTippy
                                    placement="right"
                                    triggers="mouseenter"
                                >
                                    <div
                                        :key="item"
                                        class="icon-hover relative flex h-10 items-center gap-4 px-4 text-base font-normal no-underline"
                                    >
                                        <SvgIcon
                                            :class="[
                                                {
                                                    'text-brand':
                                                        open &&
                                                        item.subitems.some((subitem) => subIsActive(subitem.route)),
                                                },
                                                { 'text-muval-gray-1 dark:text-white': !isActive },
                                            ]"
                                            class="z-10 h-6 w-6 flex-shrink-0 transition"
                                            :name="item.icon"
                                        />
                                        <div
                                            class="bg-icon absolute left-0 top-0 z-0 hidden h-10 w-[63px] bg-muval-gray-6"
                                        ></div>
                                        <div
                                            v-if="item.meta?.count && !open"
                                            class="z-50 flex size-[18px] items-center justify-center rounded-full bg-brand text-xs font-bold text-white"
                                            style="margin-left: -14px"
                                        >
                                            {{ item.meta?.count }}
                                        </div>
                                    </div>

                                    <template #content>
                                        {{ item.name }}
                                    </template>
                                </MuvalTippy>
                            </DisclosureButton>

                            <div v-show="isPanelOpen(idx)">
                                <DisclosurePanel
                                    static
                                    class="disclosure-panel visible absolute inset-x-auto inset-y-0 -right-[130px] z-[9999] min-w-[200px]"
                                    as="div"
                                >
                                    <div class="flex flex-col rounded-muval-2 border bg-white shadow-muval-1">
                                        <div class="border-b border-[#E5E7EB] px-4 py-2 text-sm font-medium">
                                            {{ item.name }}
                                        </div>
                                        <router-link
                                            v-for="(subitem, subItemIndex) in item.subitems"
                                            :key="subItemIndex"
                                            v-slot="{ isActive }"
                                            :to="{ path: subitem.route }"
                                            @click="
                                                () => {
                                                    togglePanel(idx);
                                                    canRefresh(subitem.route) && $router.go();
                                                }
                                            "
                                            @click.ctrl="modClick(subitem.route)"
                                            @click.meta="modClick(subitem.route)"
                                        >
                                            <div
                                                v-if="subitem && subitem.name != 'Scheduler'"
                                                :key="subitem"
                                                class="flex items-center gap-x-4 px-4 py-2 text-sm font-normal text-focus no-underline hover:bg-muval-gray-6"
                                            >
                                                <div class="flex flex-shrink-0 items-center justify-center">
                                                    <div
                                                        class="h-1.5 w-1.5 rounded-full"
                                                        :class="[isActive ? 'bg-brand' : 'bg-muval-gray-4']"
                                                    ></div>
                                                </div>

                                                <div
                                                    v-if="subitem.meta?.count"
                                                    class="flex size-[18px] items-center justify-center rounded-full bg-brand text-xs font-bold text-white"
                                                >
                                                    {{ subitem.meta?.count }}
                                                </div>

                                                {{ subitem.name }}
                                            </div>
                                        </router-link>
                                    </div>
                                </DisclosurePanel>
                            </div>
                        </div>
                    </template>
                </component>
            </template>
        </div>
    </nav>
</template>
<script setup>
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue';
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { useJobCounts } from '@composables/useJobCounts';
import { usePendingTasksCount } from '@composables/useTodaysTasks';
const { newLeadsCount, pendingJobsCount, conflictsCount } = useJobCounts();
const { count: pendingTasksCount } = usePendingTasksCount();
const userStore = useUserStore();
const route = useRoute();
const router = useRouter();

// Track open state for each panel separately
const openPanels = ref(new Map());

// Update navigation items with counts
const navigationWithCounts = computed(() => {
    if (!userStore.navigation) return [];

    // Check if user should see count badges
    const shouldShowCounts = userStore.shouldShowCountBadges();

    return userStore.navigation.map((item) => {
        const updatedItem = { ...item };

        // Add count to Leads navigation (only if user should see counts)
        if ((item.route === '/orders' || item.name === 'Leads') && shouldShowCounts) {
            updatedItem.meta = {
                ...updatedItem.meta,
                count: newLeadsCount.value > 0 ? newLeadsCount.value : null,
            };
        }

        // Add count to Jobs navigation (only if user should see counts and route is /jobs)
        if (item.route === '/jobs' && shouldShowCounts) {
            updatedItem.meta = {
                ...updatedItem.meta,
                count: pendingJobsCount.value > 0 ? pendingJobsCount.value : null,
            };
        }

        // Crew badge: tasks awaiting acceptance. Not gated by shouldShowCountBadges —
        // that helper deliberately excludes FIELD_STAFF/CONTRACTOR (it's for leads/jobs).
        if (item.route === '/field-staff/notifications') {
            updatedItem.meta = {
                ...updatedItem.meta,
                count: pendingTasksCount.value > 0 ? pendingTasksCount.value : null,
            };
        }

        // Handle subitems if they exist
        if (item.subitems) {
            // Ensure subitems is an array
            const subitemsArray = Array.isArray(item.subitems) ? item.subitems : Object.values(item.subitems);
            updatedItem.subitems = subitemsArray.map((subitem) => {
                const updatedSubitem = { ...subitem };

                if ((subitem.route === '/orders' || subitem.name === 'Leads') && shouldShowCounts) {
                    updatedSubitem.meta = {
                        ...updatedSubitem.meta,
                        count: newLeadsCount.value > 0 ? newLeadsCount.value : null,
                    };
                }

                if (subitem.route === '/jobs' && shouldShowCounts) {
                    updatedSubitem.meta = {
                        ...updatedSubitem.meta,
                        count: pendingJobsCount.value > 0 ? pendingJobsCount.value : null,
                    };
                }

                if (subitem.route === '/scheduler/fix-list') {
                    updatedSubitem.meta = {
                        ...updatedSubitem.meta,
                        count: conflictsCount.value > 0 ? conflictsCount.value : null,
                    };
                }

                if (subitem.route === '/field-staff/notifications') {
                    updatedSubitem.meta = {
                        ...updatedSubitem.meta,
                        count: pendingTasksCount.value > 0 ? pendingTasksCount.value : null,
                    };
                }

                return updatedSubitem;
            });

            // Sum subitem counts onto the parent (shown when disclosure is closed)
            // Exclude fix list count — it should only appear when the menu is expanded
            const subitemTotal = updatedItem.subitems
                .filter((sub) => sub.route !== '/scheduler/fix-list')
                .reduce((sum, sub) => sum + (sub.meta?.count ?? 0), 0);
            if (subitemTotal > 0) {
                updatedItem.meta = { ...updatedItem.meta, count: subitemTotal };
            }
        }

        return updatedItem;
    });
});

const togglePanel = (itemId) => {
    // Close any other open panels first
    openPanels.value.forEach((_, key) => {
        if (key !== itemId) {
            openPanels.value.set(key, false);
        }
    });
    // Toggle the clicked panel
    openPanels.value.set(itemId, !openPanels.value.get(itemId));
};

const isPanelOpen = (itemId) => {
    return openPanels.value.get(itemId) || false;
};

// Handle clicks outside the panel
const handleClickOutside = (event) => {
    const panels = document.querySelectorAll('.disclosure-panel');
    const buttons = document.querySelectorAll('.disclosure-button');
    let clickedInsideComponent = false;

    panels.forEach((panel) => {
        if (panel.contains(event.target)) {
            clickedInsideComponent = true;
        }
    });

    buttons.forEach((button) => {
        if (button.contains(event.target)) {
            clickedInsideComponent = true;
        }
    });

    if (!clickedInsideComponent) {
        openPanels.value.forEach((_, key) => {
            openPanels.value.set(key, false);
        });
    }
};

onMounted(() => {
    document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
});

watch(
    () => route.fullPath,
    () => {
        openPanels.value.forEach((_, key) => {
            openPanels.value.set(key, false);
        });
    },
);

function subIsActive(input) {
    const paths = Array.isArray(input) ? input : [input];
    if (route.name == 'Order' && router?.options?.history?.state?.back == '/jobs' && input == '/jobs') {
        return true;
    } else if (route.name == 'Order' && router?.options?.history?.state?.back == '/jobs' && input == '/orders') {
        return false;
    } else {
        return paths.some((path) => {
            return route.path.indexOf(path) === 0; // current path starts with this path string
        });
    }
}

function modClick(route) {
    window.open(route, '_blank');
}

function canRefresh(input) {
    return route.path === input;
}
</script>

<style scoped>
/* ::v-deep(.tippy-box) {
    margin-left: -80px !important;
} */
.icon-hover:hover .bg-icon {
    @apply block;
}
</style>
